/**
 * The public application form — SmartSpaza's front door.
 *
 * The only screen in the application that renders for somebody with no account,
 * so it carries none of the shell: no sidebar, no branch selector, no till. A
 * person here is not operating a shop yet; they are asking to.
 *
 * **Written for a shopkeeper on a phone.** One column on a small screen,
 * ordinary words, and the fields in the order somebody would say them out loud —
 * the shop, then themselves, then where it is, then how big. Optional fields say
 * so, because a form that looks long is a form people abandon, and the only ones
 * that genuinely block a review are the name, the contact and a way to reach them.
 *
 * The reference is the whole point of the success state. It is the only thing
 * tying this person to their application afterwards, so it is large and
 * selectable — losing it is the commonest way somebody ends up applying twice.
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { Button, Card, SelectField, TextField } from "@/components/ui";
import {
  applicationRepository,
  type ApplicationForm,
  type SubmissionReceipt,
} from "@/repositories/applicationRepository";

const BUSINESS_TYPES = [
  { value: "SPAZA", label: "Spaza shop" },
  { value: "MINI_MARKET", label: "Mini market" },
  { value: "SUPERMARKET", label: "Supermarket" },
  { value: "TAKEAWAY", label: "Takeaway / kitchen" },
  { value: "BUTCHERY", label: "Butchery" },
  { value: "LIQUOR", label: "Liquor store" },
  { value: "HARDWARE", label: "Hardware" },
  { value: "PHARMACY", label: "Pharmacy" },
  { value: "OTHER", label: "Something else" },
];

const CHANNELS = [
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "PHONE", label: "A phone call" },
  { value: "EMAIL", label: "Email" },
  { value: "SMS", label: "SMS" },
];

const EMPTY: ApplicationForm = {
  trading_name: "",
  legal_name: "",
  registration_number: "",
  tax_number: "",
  business_type: "SPAZA",
  description: "",
  phone: "",
  email: "",
  website: "",
  contact_first_name: "",
  contact_last_name: "",
  contact_email: "",
  contact_mobile: "",
  preferred_channel: "WHATSAPP",
  address_line1: "",
  address_line2: "",
  suburb: "",
  city: "",
  province: "",
  postal_code: "",
  expected_branches: 1,
  expected_tills: 1,
  expected_users: 2,
  needs_inventory: true,
  needs_procurement: false,
  needs_reporting: true,
};

export function ApplyPage() {
  const [form, setForm] = useState<ApplicationForm>(EMPTY);
  const [receipt, setReceipt] = useState<SubmissionReceipt | null>(null);

  const submit = useMutation({
    mutationFn: (input: ApplicationForm) => applicationRepository.submit(input),
    onSuccess: setReceipt,
  });

  const error = submit.error instanceof ApiError ? submit.error : null;
  const fieldError = (name: keyof ApplicationForm) => error?.fieldErrors?.[name]?.[0];

  const set = <K extends keyof ApplicationForm>(name: K, value: ApplicationForm[K]) =>
    setForm((previous) => ({ ...previous, [name]: value }));

  // A duplicate is not really a failure: it is somebody who lost the first
  // acknowledgement. The server sends back the reference they already have, so
  // this hands it to them rather than asking them to try again.
  // `extra` is typed as unknown values — the server's error contract carries
  // arbitrary detail — so the reference is narrowed rather than stringified.
  const carried = error?.status === 409 ? error.extra?.reference : undefined;
  const existing = typeof carried === "string" ? carried : "";

  if (receipt) return <Submitted reference={receipt.reference} />;
  if (existing) return <Submitted reference={existing} alreadyHad />;

  return (
    <Shell
      title="Apply to use SmartSpaza"
      lead="Tell us about your shop. It takes a few minutes, and we will be in touch."
    >
      <form
        className="space-y-6"
        onSubmit={(event) => {
          event.preventDefault();
          submit.mutate(form);
        }}
      >
        {error && error.status !== 409 && (
          <p className="rounded-card bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error.detail || "Something went wrong. Please check the form and try again."}
          </p>
        )}

        <Section title="Your shop">
          <TextField
            label="Shop name"
            required
            value={form.trading_name}
            error={fieldError("trading_name")}
            hint="The name above the door."
            onChange={(e) => set("trading_name", e.target.value)}
          />
          <SelectField
            label="What kind of shop is it?"
            options={BUSINESS_TYPES}
            value={form.business_type}
            onChange={(e) => set("business_type", e.target.value)}
          />
          <TextField
            label="Registered company name"
            value={form.legal_name}
            hint="Optional. Only if the business is registered."
            onChange={(e) => set("legal_name", e.target.value)}
          />
          <TextField
            label="Company registration number"
            value={form.registration_number}
            hint="Optional."
            onChange={(e) => set("registration_number", e.target.value)}
          />
          <TextField
            label="Shop phone number"
            required
            inputMode="tel"
            value={form.phone}
            error={fieldError("phone")}
            onChange={(e) => set("phone", e.target.value)}
          />
          <TextField
            label="Shop email address"
            required
            type="email"
            value={form.email}
            error={fieldError("email")}
            hint="We send your reference and updates here."
            onChange={(e) => set("email", e.target.value)}
          />
        </Section>

        <Section title="About you">
          <TextField
            label="First name"
            required
            value={form.contact_first_name}
            error={fieldError("contact_first_name")}
            onChange={(e) => set("contact_first_name", e.target.value)}
          />
          <TextField
            label="Last name"
            required
            value={form.contact_last_name}
            error={fieldError("contact_last_name")}
            onChange={(e) => set("contact_last_name", e.target.value)}
          />
          <TextField
            label="Your mobile number"
            required
            inputMode="tel"
            value={form.contact_mobile}
            error={fieldError("contact_mobile")}
            onChange={(e) => set("contact_mobile", e.target.value)}
          />
          <TextField
            label="Your email address"
            required
            type="email"
            value={form.contact_email}
            error={fieldError("contact_email")}
            onChange={(e) => set("contact_email", e.target.value)}
          />
          <SelectField
            label="How should we contact you?"
            options={CHANNELS}
            value={form.preferred_channel}
            onChange={(e) => set("preferred_channel", e.target.value)}
          />
        </Section>

        <Section title="Where the shop is">
          <TextField
            label="Street address"
            value={form.address_line1}
            hint="Where a delivery driver would go."
            onChange={(e) => set("address_line1", e.target.value)}
          />
          <TextField
            label="Suburb or township"
            value={form.suburb}
            onChange={(e) => set("suburb", e.target.value)}
          />
          <TextField
            label="Town or city"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
          />
          <TextField
            label="Province"
            value={form.province}
            onChange={(e) => set("province", e.target.value)}
          />
          <TextField
            label="Postal code"
            inputMode="numeric"
            value={form.postal_code}
            onChange={(e) => set("postal_code", e.target.value)}
          />
        </Section>

        <Section
          title="How big is it?"
          note="A rough answer is fine — it only helps us suggest the right package."
        >
          <TextField
            label="How many shops?"
            type="number"
            min={1}
            value={form.expected_branches}
            error={fieldError("expected_branches")}
            onChange={(e) => set("expected_branches", Number(e.target.value))}
          />
          <TextField
            label="How many tills?"
            type="number"
            min={1}
            value={form.expected_tills}
            error={fieldError("expected_tills")}
            onChange={(e) => set("expected_tills", Number(e.target.value))}
          />
          <TextField
            label="How many people will use it?"
            type="number"
            min={1}
            value={form.expected_users}
            error={fieldError("expected_users")}
            onChange={(e) => set("expected_users", Number(e.target.value))}
          />
        </Section>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" size="lg" loading={submit.isPending}>
            Send my application
          </Button>
          <Link to="/apply/status" className="text-sm text-ink-600 hover:text-ink-900">
            Already applied? Check on it
          </Link>
        </div>
      </form>
    </Shell>
  );
}

/**
 * The page furniture for somebody with no account.
 *
 * Deliberately plain. There is no navigation to offer — every other screen in
 * this application needs a session — so the only ways out are the form itself
 * and the sign-in page, which are the two places somebody here would want to go.
 */
export function Shell({
  title,
  lead,
  children,
}: {
  title: string;
  lead: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas px-4 py-8 sm:py-14">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-6">
          <p className="text-sm font-semibold tracking-wide text-brand-600">SMARTSPAZA</p>
          <h1 className="mt-1 text-2xl font-semibold text-ink-900">{title}</h1>
          <p className="mt-1.5 text-sm text-ink-600">{lead}</p>
        </header>

        <Card>{children}</Card>

        <p className="mt-5 text-center text-sm text-ink-500">
          Already have an account?{" "}
          <Link to="/login" className="text-brand-600 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <legend className="text-sm font-semibold text-ink-900">{title}</legend>
      {note && <p className="text-sm text-ink-500">{note}</p>}
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function Submitted({ reference, alreadyHad }: { reference: string; alreadyHad?: boolean }) {
  return (
    <Shell
      title={alreadyHad ? "You have already applied" : "Thank you — we have it"}
      lead={
        alreadyHad
          ? "There is already an application open for that email address. Here is its reference."
          : "We will look at your application and be in touch."
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-sm text-ink-500">Your reference</p>
          {/* Selectable and large. Losing this is the commonest reason somebody
              applies twice, and a duplicate is a queue entry a person has to
              work out is a duplicate. */}
          <p className="mt-1 select-all font-mono text-2xl font-semibold text-ink-900">
            {reference}
          </p>
        </div>

        <p className="text-sm text-ink-600">
          Keep it somewhere safe — quote it if you contact us. We have sent it to your email
          address as well.
        </p>

        <Link
          to={`/apply/status?reference=${encodeURIComponent(reference)}`}
          className="inline-flex h-touch items-center rounded-control bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
        >
          Check on my application
        </Link>
      </div>
    </Shell>
  );
}
